import * as d3 from "d3";
import { updatedScheme10 } from "../../../util/chartColorScheme";
import "./D3RepPerformanceStack.scss";
import { store } from "../../../util/store";
import { formatTeamsData } from "../../../components/ChartBuilder/teamFilterComponents";

/**
 * D3RepPerformanceStack Component
 * 
 * A hierarchical stacked bar chart visualization for rep performance data.
 * Features:
 * - Builds a JSON model from nested team data and performance metrics
 * - Renders interactive drillable stacked bars
 * - Shows ACV (Annual Contract Value) in styled HTML tooltip
 * - Tooltip follows mouse pointer and clamps to viewport
 * - Smart label rendering with text truncation to maximize visible text
 * - Removes level headings for compact display
 * - X-axis with percentage markers from 0 to 100 (hidden in "parent" mode)
 * - Toggle between "Overall Total" and "Parent Total" percentage views
 */
const D3RepPerformanceStack = {};

/**
 * Creates and renders the performance stack visualization
 * @param {HTMLElement} containerElement - DOM element to render the chart into
 * @param {Object} chartConfig - Configuration object containing data and team filters
 * @param {Array} chartConfig.data - Raw performance data for teams/reps
 * @param {Array} chartConfig.teamFilterData - Nested team hierarchy structure
 */
D3RepPerformanceStack.create = (containerElement, chartConfig) => {
  // Clear any existing content first
  d3.select(containerElement).selectAll("*").remove();

  // Check if data is loaded
  if (!chartConfig || !chartConfig.data || !Array.isArray(chartConfig.data) || chartConfig.data.length === 0) {
    // Display a message indicating no data is available
    d3.select(containerElement)
      .append("div")
      .style("padding", "40px")
      .style("text-align", "center")
      .style("color", "#666")
      .style("font-family", "Arial, sans-serif")
      .style("font-size", "16px")
      .text("");
    return;
  }

  // Get user's currency settings from global store
  const applicationState = store.getState();
  const currencySymbol = applicationState.app.user.Currency;

  const currencyCode =
    applicationState?.app?.user?.Currency_Code ||
    applicationState?.app?.user?.CurrencyCode ||
    "USD";
  
  // Create an Intl.NumberFormat for proper currency formatting
  const currencyFormatFunction =
    typeof Intl !== "undefined" && Intl.NumberFormat
      ? new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currencyCode,
          maximumFractionDigits: 2,
        })
      : null;

  // Extract team hierarchy and performance data from config
  const teamHierarchyData = Array.isArray(chartConfig.teamFilterData)
    ? chartConfig.teamFilterData
    : [];
  const performanceMetricsArray = chartConfig.data;

  /**
   * Normalizes a team name to lowercase for case-insensitive comparison
   * @param {string} teamName - Team name to normalize
   * @returns {string} Normalized lowercase team name key
   */
  const createTeamNameKey = (teamName) => (teamName || "").toString().trim().toLowerCase();
  
  /**
   * Checks if a team name represents "All" teams filter (case-insensitive)
   * @param {string} teamName - Team name to check
   * @returns {boolean} True if the name is "all"
   */
  const isAllTeamsFilter = (teamName) => (teamName || "").toString().trim().toLowerCase() === "all";

  /**
   * Creates a lookup map from performance data array keyed by normalized team name
   * @param {Array} performanceDataArray - Array of performance data objects
   * @returns {Object} Map of normalized team name -> performance data item
   */
  function createPerformanceDataLookup(performanceDataArray) {
    return performanceDataArray.reduce((performanceDataLookup, performanceDataItem) => {
      const teamNameKey = createTeamNameKey(performanceDataItem.Display_Name || performanceDataItem.Name);
      if (!performanceDataLookup[teamNameKey]) performanceDataLookup[teamNameKey] = performanceDataItem;
      return performanceDataLookup;
    }, {});
  }

  /**
   * Transforms nested team hierarchy structure into normalized tree format
   * Each node tracks its parent name and children recursively
   * @param {Array} teamHierarchy - Array of nested team hierarchy objects
   * @returns {Array} Transformed tree structure with Display_Name and children
   */
  function transformNestedTeamHierarchy(teamHierarchy) {
    const transformedRootNodes = [];
    
    function transformTeamNode(teamNode, parentName = null) {
      const teamName = teamNode.Parent;
      const childTeamNodes = Array.isArray(teamNode.teams) ? teamNode.teams : [];
      return {
        Display_Name: teamName,
        _sourceParent: parentName,
        children: childTeamNodes.map((childTeamNode) => transformTeamNode(childTeamNode, teamName)),
      };
    }
    
    teamHierarchy.forEach((rootTeamNode) => transformedRootNodes.push(transformTeamNode(rootTeamNode, null)));
    return transformedRootNodes;
  }

  /**
   * Enriches team tree nodes with performance metrics from lookup map
   * @param {Array} teamTreeRoots - Root nodes of the team tree structure
   * @param {Object} performanceDataLookup - Map of normalized team names to performance data
   * @returns {Array} Team tree with metrics attached to each node
   */
  function enrichTeamNodesWithMetrics(teamTreeRoots, performanceDataLookup) {
    function traverseAndAttachMetrics(teamNode) {
      const teamNameKey = createTeamNameKey(teamNode.Display_Name);
      const performanceData = performanceDataLookup[teamNameKey] || {};
      teamNode.contributionToTotalPercentRaw = Number(performanceData.contributionToTotalPercentRaw) || 0;
      teamNode.contributionToParentPercent = Number(performanceData.contributionToParentPercent) || 0;
      teamNode.acv = Number(performanceData.acv) || 0;
      teamNode.attainmentPercent = Number(performanceData.attainmentPercent) || 0;
      if (teamNode.children && teamNode.children.length) teamNode.children.forEach(traverseAndAttachMetrics);
    }
    teamTreeRoots.forEach(traverseAndAttachMetrics);
    return teamTreeRoots;
  }

  /**
   * Sorts team children within each node by contribution percentage (descending)
   * @param {Array} teamTreeRoots - Root nodes of the team tree structure
   * @returns {Array} Team tree with sorted children
   */
  function sortTeamChildrenByContribution(teamTreeRoots) {
    function sortChildrenRecursively(teamNode) {
      if (teamNode.children && teamNode.children.length) {
        teamNode.children.forEach(sortChildrenRecursively);
        teamNode.children.sort(
          (childA, childB) =>
            (childB.contributionToTotalPercentRaw || 0) -
            (childA.contributionToTotalPercentRaw || 0)
        );
      }
    }
    teamTreeRoots.forEach(sortChildrenRecursively);
    return teamTreeRoots;
  }

  /**
   * Recursively promotes children of nodes with zero contribution to fill their parent's position.
   * Works on an array (current displayed level) and repeats until every node has own contribution or is a leaf.
   * @param {Array} teamNodes - Array of team nodes to process
   * @returns {Array} Array with promoted children replacing zero-contribution parents
   */
  function promoteChildrenOfZeroContributionNodes(teamNodes) {
    const hasNonZeroContribution = (teamNode) => (Number(teamNode.contributionToTotalPercentRaw) || 0) > 0;
    let currentLevelNodes = teamNodes.slice();

    const trackPromotionSource = (childNode, promotedFromName) => {
      if (childNode && typeof childNode === "object" && promotedFromName) {
        const existing = childNode._sourcePromotedFrom;
        childNode._sourcePromotedFrom = existing ? `${existing} > ${promotedFromName}` : promotedFromName;
      }
    };

    let safetyCounter = 0;
    while (safetyCounter++ < 1000) {
      let wasPromoted = false;
      const nextLevelNodes = [];
      for (const teamNode of currentLevelNodes) {
        const hasChildren = Array.isArray(teamNode.children) && teamNode.children.length > 0;
        if (!hasNonZeroContribution(teamNode) && hasChildren) {
          teamNode.children.forEach((childNode) => {
            trackPromotionSource(childNode, teamNode.Display_Name);
            nextLevelNodes.push(childNode);
          });
          wasPromoted = true;
        } else {
          nextLevelNodes.push(teamNode);
        }
      }
      currentLevelNodes = nextLevelNodes;
      if (!wasPromoted) break;
    }
    return currentLevelNodes;
  }

  /**
   * Adds missing contribution nodes when parent contribution exceeds sum of children contributions.
   * @param {Array} teamTreeRoots - Root nodes of the team tree structure
   * @returns {Array} Team tree with missing contribution nodes added
   */
  function addMissingContributionNodes(teamTreeRoots) {
    const calculateTotalContributionPercent = (teamNodeArray) =>
      teamNodeArray.reduce(
        (total, childNode) => total + (Number(childNode.contributionToTotalPercentRaw) || 0),
        0
      );
    const calculateTotalACV = (teamNodeArray) => 
      teamNodeArray.reduce((total, childNode) => total + (Number(childNode.acv) || 0), 0);

    const nodesQueue = [];
    teamTreeRoots.forEach((rootTeamNode) => nodesQueue.push(rootTeamNode));
    
    while (nodesQueue.length) {
      const currentTeamNode = nodesQueue.shift();
      if (currentTeamNode.children && currentTeamNode.children.length) {
        currentTeamNode.children.forEach((childTeamNode) => nodesQueue.push(childTeamNode));
        
        const parentContributionPercent = Number(currentTeamNode.contributionToTotalPercentRaw) || 0;
        const childrenContributionTotal = calculateTotalContributionPercent(currentTeamNode.children);
        const missingContributionPercent = parentContributionPercent - childrenContributionTotal;

        if (missingContributionPercent > 0.000001) {
          const parentACVValue = Number(currentTeamNode.acv) || 0;
          const childrenACVTotal = calculateTotalACV(currentTeamNode.children);
          let missingACVValue = parentACVValue - childrenACVTotal;

          if (missingACVValue <= 0 && parentContributionPercent > 0 && childrenContributionTotal > 0) {
            const contributionScaleFactor = parentContributionPercent / childrenContributionTotal;
            missingACVValue = Math.max(0, childrenACVTotal * (contributionScaleFactor - 1));
          }

          const missingParentPercent = parentContributionPercent > 0
            ? (missingContributionPercent / parentContributionPercent) * 100
            : 0;
          currentTeamNode.children.push({
            Display_Name: `${currentTeamNode.Display_Name} - Other`,
            contributionToTotalPercentRaw: missingContributionPercent,
            contributionToParentPercent: missingParentPercent,
            acv: Math.max(0, missingACVValue),
            attainmentPercent: 0,
            children: [],
          });
        }
      }
    }
    return teamTreeRoots;
  }

  /**
   * Filters root team nodes, skipping "All" filter if it's the only root
   * @param {Array} teamRootNodes - Array of root team nodes
   * @returns {Array} Filtered root nodes excluding "All" filter
   */
  function filterVisibleRootNodes(teamRootNodes) {
    if (teamRootNodes.length === 1 && isAllTeamsFilter(teamRootNodes[0].Display_Name)) {
      return teamRootNodes[0].children || [];
    }
    return teamRootNodes.filter((rootTeamNode) => !isAllTeamsFilter(rootTeamNode.Display_Name));
  }

  /**
   * Merges visible root nodes into a single "Total" tree for visualization
   * Assigns colors to each node based on its team name
   * @param {Array} teamRootNodes - Array of root team nodes
   * @returns {Object} Unified total tree with all roots as children
   */
  function createUnifiedTotalTree(teamRootNodes) {
    // Ordinal color scale for categories (idiomatic in D3)
    const teamColorScale = d3.scaleOrdinal(updatedScheme10);
    const getTeamColorFromScale = (teamName) => teamColorScale(teamName || "unknown");

    const filteredRootNodes = filterVisibleRootNodes(teamRootNodes);
    
    const totalContributionPercent = filteredRootNodes.reduce(
      (sum, teamNode) => sum + (Number(teamNode.contributionToTotalPercentRaw) || 0),
      0
    );

    function transformDataNodeToTreeNode(teamDataNode) {
      const teamName = teamDataNode.Display_Name || "";
      const contributionPercent = Number(teamDataNode.contributionToTotalPercentRaw) || 0;
      const teamColor = getTeamColorFromScale(teamName);
      const childTreeNodeArray = (teamDataNode.children || []).map(transformDataNodeToTreeNode);
      
      return {
        name: teamName,
        value: contributionPercent,
        color: teamColor,
        rawPercent: contributionPercent,
        parentPercent: Number(teamDataNode.contributionToParentPercent) || 0,
        acv: Number(teamDataNode.acv) || 0,
        children: childTreeNodeArray,
        _sourcePromotedFrom: teamDataNode._sourcePromotedFrom,
      };
    }

    const rootLevelChildren = filteredRootNodes.map(transformDataNodeToTreeNode);
    
    return {
      name: "Total",
      value: totalContributionPercent,
      color: "#666",
      rawPercent: totalContributionPercent,
      parentPercent: 0,
      acv: rootLevelChildren.reduce((sum, childNode) => sum + (childNode.acv || 0), 0),
      children: rootLevelChildren,
    };
  }

  /**
   * Builds a level-by-level representation of the tree for rendering stacked bars
   * @param {Object} teamTreeNode - Root tree node to build levels from
   * @param {number} totalContributionValue - Total contribution value for percentage calculations
   * @returns {Array} Array of levels, each containing segment data for that level
   */
  function createLevelsArrayFromTree(teamTreeNode, totalContributionValue = teamTreeNode.value) {
    const chartLevelsArray = [];
    
    function traverseTreeAndBuildLevels(teamNode, startPositionPercent, levelIndex, parentNode = null) {
      if (!chartLevelsArray[levelIndex]) chartLevelsArray[levelIndex] = [];
      if (teamNode.children && teamNode.children.length) {
        let cumulativeStartPercent = startPositionPercent;
        
        teamNode.children.forEach((childTeamNode) => {
          const segmentWidthPercent = (childTeamNode.value / totalContributionValue) * 100;
          chartLevelsArray[levelIndex].push({
            name: childTeamNode.name,
            color: childTeamNode.color,
            rawPercent: childTeamNode.rawPercent,
            parentPercent: childTeamNode.parentPercent,
            acv: childTeamNode.acv || 0,
            start: cumulativeStartPercent,
            width: segmentWidthPercent,
            node: childTeamNode,
            parentNode: teamNode,
          });
          if (childTeamNode.children && childTeamNode.children.length) {
            traverseTreeAndBuildLevels(childTeamNode, cumulativeStartPercent, levelIndex + 1, childTeamNode);
          }
          cumulativeStartPercent += segmentWidthPercent;
        });
      }
    }
    
    traverseTreeAndBuildLevels(teamTreeNode, 0, 0);
    return chartLevelsArray;
  }

  // Step 1: Transform the nested team hierarchy structure
  const transformedTeamRoots = transformNestedTeamHierarchy(teamHierarchyData);
  // Step 2: Create lookup map for fast performance data access
  const performanceDataLookup = createPerformanceDataLookup(performanceMetricsArray);
  // Step 3: Enrich team nodes with performance metrics
  enrichTeamNodesWithMetrics(transformedTeamRoots, performanceDataLookup);
  // Step 4: Sort team children by contribution (highest first)
  sortTeamChildrenByContribution(transformedTeamRoots);

  // Step 4.1: Recursively promote children of zero-contribution nodes until displayable
  const promotedTeamRoots = promoteChildrenOfZeroContributionNodes(transformedTeamRoots);

  // Step 5: Add missing contribution nodes to reconcile parent-child differences
  addMissingContributionNodes(promotedTeamRoots);
  // Step 6: Merge roots into a single unified tree under "Total"
  const totalTeamTree = createUnifiedTotalTree(promotedTeamRoots);
  // Step 7: Build level-by-level representation for rendering
  const chartLevelsModel = createLevelsArrayFromTree(totalTeamTree, totalTeamTree.value);

  // Store complete data model in config for reference
  const chartDataModel = {
    meta: {
      currencySymbol: currencySymbol,
      currencyCode: currencyCode,
      builtAt: new Date().toISOString(),
    },
    roots: promotedTeamRoots.map((rootTeamNode) => rootTeamNode.Display_Name),
    tree: totalTeamTree,
    levels: chartLevelsModel,
  };

  chartConfig.jsonModel = chartDataModel;
  console.log("[D3RepStack] JSON model", chartDataModel);

  // Create main container for the chart
  const chartContainer = d3
    .select(containerElement)
    .append("div")
    .attr("class", "drilldown-root")
    .style("max-width", "1200px")
    .style("margin", "0 auto")
    .style("padding", "30px")
    .style("background-color", "white")
    .style("font-family", "Arial, sans-serif");

  // Get display mode from chartConfig (defaults to "overall")
  const displayMode = chartConfig.totalMode || "overall"; // "overall" or "parent"

  // Hidden element for measuring text width (must exist before first render)
  const textMeasurementElement = chartContainer
    .append("span")
    .style("position", "absolute")
    .style("visibility", "hidden")
    .style("white-space", "nowrap")
    .style("font-family", "Arial, sans-serif")
    .style("font-size", "0.85rem")
    .style("font-weight", "600")
    .style("padding", "0 2px")
    .style("left", "-9999px");

  const measureTextWidth = (textToMeasure) => {
    textMeasurementElement.text(textToMeasure);
    return textMeasurementElement.node()?.offsetWidth || 0;
  };
  
  // Create HTML tooltip that follows mouse pointer
  const tooltipElement = d3
    .select(containerElement)
    .append("div")
    .attr("class", "tooltip")
    .style("opacity", 0);

  function formatACVValue(acv) {
    const acvNumeric = Number(acv || 0);
    if (currencyFormatFunction) return currencyFormatFunction.format(acvNumeric);
    return `${currencySymbol}${d3.format(",")(Math.round(acvNumeric))}`;
  }

  function displayTooltipAtPosition(segmentColor, segmentName, contributionPercentage, acv, event, parentContributionPercent = null) {
    const contributionPercent = contributionPercentage;
    const mouseEvent = event || d3.event;
    tooltipElement.transition().duration(200).style("opacity", 1);
    
    const formattedPercentage = parentContributionPercent !== null 
      ? `${Number(parentContributionPercent || 0).toFixed(1)}% (of parent)`
      : `${Number(contributionPercent || 0).toFixed(1)}%`;
    
    tooltipElement
      .html(`
        <div style="border-top:10px solid ${segmentColor};border-radius:1rem;">
          <div class="tooltip-box">
            <h3 class="heading-style">${segmentName}</h3>
            <hr>
            <div class="container">
              <h4 class="result-style">
                Contribution Percent: <span style="color:${segmentColor}">${formattedPercentage}</span>
              </h4>
            </div>
            <hr>
            <div class="container">
              <h3 class="heading-style">${currencySymbol}${d3.format(",")(Math.round(acv || 0))}</h3>
            </div>
          </div>
        </div>
      `)
      .style("left", mouseEvent.pageX + "px")
      .style("top", mouseEvent.pageY - 28 + "px");
  }

  function updateTooltipPosition(event) {
    const mouseEvent = event || d3.event;
    tooltipElement
      .style("left", mouseEvent.pageX + "px")
      .style("top", mouseEvent.pageY - 28 + "px");
  }

  function hideTooltip() {
    tooltipElement.transition().duration(300).style("opacity", 0);
  }
  
  // Container for all level rows
  const levelsContainer = chartContainer.append("div").attr("class", "levels-host");

  // Add x-axis container (always create it to maintain consistent layout height)
  const xAxisElement = chartContainer
    .append("div")
    .attr("class", "x-axis-container")
    .style("position", "relative")
    .style("height", "30px")
    .style("margin-top", "10px")
    .style("border-top", "1px solid #ddd")
    .style("visibility", "visible");

  {

    // Create x-axis scale 
    const axisMarkerInterval = 10;
    const axisMarkers = Array.from(
      { length: Math.floor(100 / axisMarkerInterval) + 1 },
      (_, i) => i * axisMarkerInterval
    );
    axisMarkers.forEach((markerValue) => {
      xAxisElement
        .append("div")
        .style("position", "absolute")
        .style("left", `${markerValue}%`)
        .style("transform", "translateX(-50%)")
        .style("font-size", "0.85rem")
        .style("color", "#000000de")
        .style("padding-top", "5px")
        .text(`${markerValue}%`);
      
      // Add tick mark
      xAxisElement
        .append("div")
        .style("position", "absolute")
        .style("left", `${markerValue}%`)
        .style("top", "0")
        .style("width", "1px")
        .style("height", "5px")
        .style("background-color", "#999")
        .style("transform", "translateX(-50%)");
    });
  }

  // Path tracks the drill-down navigation (starts at root)
  const drillDownNavigationPath = [chartDataModel.tree];

  /**
   * Gets promoted display node when drilling into a node with zero contribution but with children.
   * Promotes its children to fill full width based on the next available level.
   * @param {Object} teamNode - Team tree node to get display node for
   * @returns {Object} Promoted display node
   */
  function getPromotedNodeForDrillDown(teamNode) {
    if (!teamNode) return teamNode;

    // Convert tree node to data-like node to reuse promotion logic
    function convertTreeNodeToDataNode(teamTreeNode) {
      return {
        Display_Name: teamTreeNode.name,
        contributionToTotalPercentRaw: Number(teamTreeNode.rawPercent) || 0,
        contributionToParentPercent: Number(teamTreeNode.parentPercent) || 0,
        acv: Number(teamTreeNode.acv) || 0,
        children: (teamTreeNode.children || []).map(convertTreeNodeToDataNode),
        _sourcePromotedFrom: teamTreeNode._sourcePromotedFrom,
      };
    }
    const convertedDataNode = convertTreeNodeToDataNode(teamNode);
    const promotedNodes = promoteChildrenOfZeroContributionNodes([convertedDataNode]);

    if (promotedNodes.length === 1) {
      const promotedNode = promotedNodes[0];
      return {
        name: promotedNode.Display_Name,
        value: Number(promotedNode.contributionToTotalPercentRaw) || 0,
        color: teamNode.color,
        rawPercent: Number(promotedNode.contributionToTotalPercentRaw) || 0,
        parentPercent: Number(promotedNode.contributionToParentPercent) || 0,
        acv: Number(promotedNode.acv) || 0,
        children: (promotedNode.children || []).map((childNode) => ({
          name: childNode.Display_Name,
          value: Number(childNode.contributionToTotalPercentRaw) || 0,
          color: teamNode.color,
          rawPercent: Number(childNode.contributionToTotalPercentRaw) || 0,
          parentPercent: Number(childNode.contributionToParentPercent) || 0,
          acv: Number(childNode.acv) || 0,
          children: (childNode.children || []).map((grandchildNode) => ({
            name: grandchildNode.Display_Name,
            value: Number(grandchildNode.contributionToTotalPercentRaw) || 0,
            color: teamNode.color,
            rawPercent: Number(grandchildNode.contributionToTotalPercentRaw) || 0,
            parentPercent: Number(grandchildNode.contributionToParentPercent) || 0,
            acv: Number(grandchildNode.acv) || 0,
            children: [],
          })),
        })),
      };
    } else {
      const totalContribution = promotedNodes.reduce(
        (sum, node) => sum + (Number(node.contributionToTotalPercentRaw) || 0),
        0
      );
      return {
        name: teamNode.name,
        value: totalContribution,
        color: teamNode.color,
        rawPercent: totalContribution,
        parentPercent: 0,
        acv: promotedNodes.reduce((sum, node) => sum + (Number(node.acv) || 0), 0),
        children: promotedNodes.map((node) => ({
          name: node.Display_Name,
          value: Number(node.contributionToTotalPercentRaw) || 0,
          color: teamNode.color,
          rawPercent: Number(node.contributionToTotalPercentRaw) || 0,
          parentPercent: Number(node.contributionToParentPercent) || 0,
          acv: Number(node.acv) || 0,
          children: (node.children || []).map((childNode) => ({
            name: childNode.Display_Name,
            value: Number(childNode.contributionToTotalPercentRaw) || 0,
            color: teamNode.color,
            rawPercent: Number(childNode.contributionToTotalPercentRaw) || 0,
            parentPercent: Number(childNode.contributionToParentPercent) || 0,
            acv: Number(childNode.acv) || 0,
            children: [],
          })),
        })),
      };
    }
  }

  /**
   * Builds levels array for current node (root or drill target)
   * @param {Object} teamTreeNode - Team tree node to build levels from
   * @param {number} totalContributionValue - Total contribution value for percentage calculations
   * @returns {Array} Array of levels, each containing segment data for that level
   */
  function createLevelsForDrillDown(teamTreeNode, totalContributionValue = teamTreeNode.value) {
    const chartLevels = [];
    function traverseNodeAndBuildLevels(teamNode, startPositionPercent, levelIndex, parentNode = null) {
      if (!chartLevels[levelIndex]) chartLevels[levelIndex] = [];
      if (teamNode.children && teamNode.children.length) {
        let currentStartPosition = startPositionPercent;
        teamNode.children.forEach((childTeamNode) => {
          const segmentWidthPercent = (childTeamNode.value / totalContributionValue) * 100;
          chartLevels[levelIndex].push({
            name: childTeamNode.name,
            color: childTeamNode.color,
            rawPercent: childTeamNode.rawPercent,
            parentPercent: childTeamNode.parentPercent,
            acv: childTeamNode.acv || 0,
            start: currentStartPosition,
            width: segmentWidthPercent,
            node: childTeamNode,
            parentNode: teamNode,
          });
          if (childTeamNode.children && childTeamNode.children.length) {
            traverseNodeAndBuildLevels(childTeamNode, currentStartPosition, levelIndex + 1, childTeamNode);
          }
          currentStartPosition += segmentWidthPercent;
        });
      }
    }
    traverseNodeAndBuildLevels(teamTreeNode, 0, 0);
    return chartLevels;
  }

  /**
   * Truncates text to fit within a given maximum width, adding ellipsis if needed
   * Uses binary search to find the optimal truncation point
   * @param {string} textToTruncate - Text to truncate
   * @param {number} maxWidthPixels - Maximum width in pixels
   * @returns {string} Truncated text with ellipsis if needed
   */
  function truncateTextToMaxWidth(textToTruncate, maxWidthPixels) {
    if (measureTextWidth(textToTruncate) <= maxWidthPixels) {
      return textToTruncate;
    }
    
    // Binary search for the right length
    let searchStart = 0;
    let searchEnd = textToTruncate.length;
    let truncatedText = "";
    
    while (searchStart <= searchEnd) {
      const midpoint = Math.floor((searchStart + searchEnd) / 2);
      const truncatedTextCandidate = textToTruncate.substring(0, midpoint) + "...";
      const textWidth = measureTextWidth(truncatedTextCandidate);
      
      if (textWidth <= maxWidthPixels) {
        truncatedText = truncatedTextCandidate;
        searchStart = midpoint + 1;
      } else {
        searchEnd = midpoint - 1;
      }
    }
    
    return truncatedText || "...";
  }

  /**
   * Renders all chart levels based on current drill-down navigation path
   */
  function renderChartLevels() {
    // Store current scroll position
    const scrollPositionX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollPositionY = window.pageYOffset || document.documentElement.scrollTop;
    
    let activeDrillNode = drillDownNavigationPath[drillDownNavigationPath.length - 1];

    // If current node has zero contribution but has children, promote for display
    const shouldPromoteChildren =
      (Number(activeDrillNode.rawPercent) || 0) === 0 &&
      activeDrillNode.children &&
      activeDrillNode.children.length > 0;

    const displayNode = shouldPromoteChildren
      ? getPromotedNodeForDrillDown(activeDrillNode)
      : activeDrillNode;

    const chartLevelsToRender =
      displayNode === chartDataModel.tree
        ? chartDataModel.levels
        : createLevelsForDrillDown(displayNode, displayNode.value);

    levelsContainer.selectAll("*").remove();

    // Map to track parent bar pixel positions and widths for child level calculations
    // Key: parent team name, Value: { leftPx: number, widthPx: number }
    const parentBarInfoMap = new Map();

    chartLevelsToRender.forEach((levelSegments, currentLevelIndex) => {
      const levelContainer = levelsContainer
        .append("div")
        .attr("class", "level-container")
        .style("margin-bottom", "5px");

      const barContainer = levelContainer
        .append("div")
        .attr("class", "bar-container")
        .style("position", "relative")
        .style("height", "50px")
        .style("background-color", "transparent")
        .style("overflow", "hidden");

      const getContainerWidthPixels = () => barContainer.node()?.clientWidth || 0;
      const containerWidthPixels = getContainerWidthPixels();

      levelSegments.forEach((segmentData) => {
        // Calculate parent percentage if in parent mode
        let displayPercentage = segmentData.rawPercent;
        let parentContributionPercent = null;
        
        if (displayMode === "parent" && segmentData.parentNode) {
          parentContributionPercent = Number(segmentData.parentPercent) || 0;
          displayPercentage = parentContributionPercent;
        }

        // Calculate segment position and width
        let segmentLeftPercent, segmentWidthPercent;
        
        if (currentLevelIndex === 0) {
          // Level 0: Use percentages relative to full container
          const clampedStartPercent = Math.max(0, Math.min(100, segmentData.start));
          const clampedEndPercent = Math.max(0, Math.min(100, segmentData.start + segmentData.width));
          const clampedWidthPercent = Math.max(0, clampedEndPercent - clampedStartPercent);
          segmentLeftPercent = clampedStartPercent;
          segmentWidthPercent = clampedWidthPercent;
        } else {
          // Child levels: Constrain bars to parent's rendered area
          if (segmentData.parentNode) {
            const parentTeamName = segmentData.parentNode.name;
            const parentBarInfo = parentBarInfoMap.get(parentTeamName);
            
            if (parentBarInfo && parentBarInfo.widthPx > 0) {
              // Get parent's position and width as percentages of total container
              const parentLeftPercent = (parentBarInfo.leftPx / containerWidthPixels) * 100;
              const parentRightPercent = parentLeftPercent + (parentBarInfo.widthPx / containerWidthPixels) * 100;
              
              // segmentData.start and segmentData.width are percentages of total (0-100%)
              // Clamp child's calculated position to parent's bounds
              const childLeftPercent = Math.max(parentLeftPercent, Math.min(parentRightPercent, segmentData.start));
              const childRightPercent = Math.max(parentLeftPercent, Math.min(parentRightPercent, segmentData.start + segmentData.width));
              const childWidthPercent = Math.max(0, childRightPercent - childLeftPercent);
              
              // Ensure child doesn't exceed parent's width
              segmentLeftPercent = childLeftPercent;
              segmentWidthPercent = Math.min(childWidthPercent, parentRightPercent - parentLeftPercent);
            } else {
              // Fallback: use original calculation if parent info not found
              const clampedStartPercent = Math.max(0, Math.min(100, segmentData.start));
              const clampedEndPercent = Math.max(0, Math.min(100, segmentData.start + segmentData.width));
              const clampedWidthPercent = Math.max(0, clampedEndPercent - clampedStartPercent);
              segmentLeftPercent = clampedStartPercent;
              segmentWidthPercent = clampedWidthPercent;
            }
          } else {
            // No parent node: use original calculation
            const clampedStartPercent = Math.max(0, Math.min(100, segmentData.start));
            const clampedEndPercent = Math.max(0, Math.min(100, segmentData.start + segmentData.width));
            const clampedWidthPercent = Math.max(0, clampedEndPercent - clampedStartPercent);
            segmentLeftPercent = clampedStartPercent;
            segmentWidthPercent = clampedWidthPercent;
          }
        }

        const segmentElement = barContainer
          .append("div")
          .attr("class", "bar-segment")
          .style("position", "absolute")
          .style("height", "100%")
          .style("display", "flex")
          .style("align-items", "center")
          .style("justify-content", "center")
          .style("color", "white")
          .style("font-size", "0.85rem")
          .style("font-weight", "600")
          .style("cursor", "pointer")
          .style("transition", "opacity 0.2s")
          .style("border-right", "2px solid white")
          .style("box-sizing", "border-box")
          .style("left", `${segmentLeftPercent}%`)
          .style("width", `${segmentWidthPercent}%`)
          .style("background-color", segmentData.color)
          .on("mouseenter", (mouseEnterEvent) => {
            const segmentDisplayName = segmentData.name || "Team";
            const contributionPercentage = Number(segmentData.rawPercent || 0);
            const acvValue = Number(segmentData.acv || 0);
            displayTooltipAtPosition(segmentData.color, segmentDisplayName, contributionPercentage, acvValue, mouseEnterEvent, parentContributionPercent);
          })
          .on("mousemove", (mouseMoveEvent) => updateTooltipPosition(mouseMoveEvent))
          .on("mouseleave", () => hideTooltip());

        const percentageLabel = `${Math.round(displayPercentage || 0)}%`;
        const teamNameLabel = segmentData.name || "";

        // Calculate actual pixel position and width of this segment
        const segmentLeftPixels = (segmentLeftPercent / 100) * containerWidthPixels;
        const segmentWidthPixels = (segmentWidthPercent / 100) * containerWidthPixels;
        
        // Store this segment's position and width for child level calculations
        parentBarInfoMap.set(segmentData.name, {
          leftPx: segmentLeftPixels,
          widthPx: segmentWidthPixels
        });

        // Minimum width threshold for showing any text (about 3-4 characters)
        const minWidthForTextDisplay = 20;
        
        // Padding to account for in calculations
        const labelHorizontalPadding = 2;
        const availableLabelWidth = segmentWidthPixels - labelHorizontalPadding;

        let labelDisplayMode = "";
        let shouldUseVerticalLayout = false;

        if (segmentWidthPixels >= minWidthForTextDisplay && availableLabelWidth > 0) {
          const nameTextWidth = measureTextWidth(teamNameLabel);
          const percentageTextWidth = measureTextWidth(percentageLabel);
          const separatorTextWidth = measureTextWidth(" ");

          // Try vertical layout first (name on top, percent below)
          if (nameTextWidth <= availableLabelWidth && percentageTextWidth <= availableLabelWidth) {
            // Both fit vertically - show both
            shouldUseVerticalLayout = true;
            labelDisplayMode = "both";
          } 
          // Try horizontal layout (name + percent on same line)
          else if (nameTextWidth + separatorTextWidth + percentageTextWidth <= availableLabelWidth) {
            // Both fit horizontally
            shouldUseVerticalLayout = false;
            labelDisplayMode = "both-horizontal";
          }
          // Try showing truncated name and percentage vertically
          else if (percentageTextWidth <= availableLabelWidth) {
            const truncatedTeamName = truncateTextToMaxWidth(teamNameLabel, availableLabelWidth);
            if (truncatedTeamName && truncatedTeamName !== "...") {
              shouldUseVerticalLayout = true;
              labelDisplayMode = "both-truncated";
            } else {
              // Name too short to show meaningfully, just show percentage
              labelDisplayMode = "percent-only";
            }
          }
          // Try showing just the percentage
          else if (percentageTextWidth <= availableLabelWidth) {
            labelDisplayMode = "percent-only";
          }
          // Try showing truncated percentage
          else {
            const truncatedPercentage = truncateTextToMaxWidth(percentageLabel, availableLabelWidth);
            if (truncatedPercentage && truncatedPercentage !== "...") {
              labelDisplayMode = "percent-truncated";
            } else {
              // Nothing fits
              labelDisplayMode = "none";
            }
          }
        }

        const labelElement = segmentElement
          .append("div")
          .attr("class", "segment-label")
          .style("display", "flex")
          .style("flex-direction", shouldUseVerticalLayout ? "column" : "row")
          .style("align-items", "center")
          .style("justify-content", "center")
          .style("height", "100%")
          .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.3)")
          .style("white-space", "nowrap")
          .style("overflow", "hidden")
          .style("text-overflow", "ellipsis")
          .style("padding", "0 4px")
          .style("font-size", "0.85rem")
          .style("width", "100%")
          .style("max-width", "100%")
          .style("box-sizing", "border-box");

        if (labelDisplayMode === "both") {
          // Vertical: name on top, percent below
          labelElement
            .append("div")
            .attr("class", "segment-name")
            .style("overflow", "hidden")
            .style("text-overflow", "ellipsis")
            .style("max-width", "100%")
            .text(teamNameLabel);

          labelElement
            .append("div")
            .attr("class", "segment-percent")
            .style("font-size", "0.8rem")
            .text(percentageLabel);
        } else if (labelDisplayMode === "both-horizontal") {
          // Horizontal: name and percent on same line
          labelElement
            .append("span")
            .attr("class", "segment-name")
            .style("margin-right", "4px")
            .text(teamNameLabel);

          labelElement
            .append("span")
            .attr("class", "segment-percent")
            .text(percentageLabel);
        } else if (labelDisplayMode === "both-truncated") {
          // Vertical: truncated name on top, percent below
          const truncatedTeamName = truncateTextToMaxWidth(teamNameLabel, availableLabelWidth);
          labelElement
            .append("div")
            .attr("class", "segment-name")
            .style("overflow", "hidden")
            .style("text-overflow", "ellipsis")
            .style("max-width", "100%")
            .text(truncatedTeamName);

          labelElement
            .append("div")
            .attr("class", "segment-percent")
            .style("font-size", "0.8rem")
            .text(percentageLabel);
        } else if (labelDisplayMode === "percent-only") {
          // Just percentage
          labelElement
            .append("div")
            .attr("class", "segment-percent")
            .text(percentageLabel);
        } else if (labelDisplayMode === "percent-truncated") {
          // Truncated percentage
          const truncatedPercentage = truncateTextToMaxWidth(percentageLabel, availableLabelWidth);
          labelElement
            .append("div")
            .attr("class", "segment-percent")
            .text(truncatedPercentage);
        }
        // If labelDisplayMode === "none", container remains empty (no space wasted)

      });
    });
    
    // Restore scroll position after rendering
    window.scrollTo(scrollPositionX, scrollPositionY);
  }
  
  // Initial render
  renderChartLevels();
};

D3RepPerformanceStack.destroy = (containerElement) => {
  d3.select(containerElement).selectAll("*").remove();
};

export default D3RepPerformanceStack;